import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Video,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Send,
  Trash2,
  CheckSquare,
  StickyNote,
} from 'lucide-react';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  addDoc,
  deleteDoc,
  onSnapshot,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { fetchAccountCalls, matchesDepartmentTitle } from '../../services/departmentCallsService';
import clientRoadmapService from '../../services/clientRoadmapService';
import { parseFathomSummarySections } from '../../utils/fathomSummaryParser';
import { getPortalEmoji } from '../../utils/portalEmojis';
import MeetingSummaryContent from '../MeetingSummaryContent';

export const PROGRESS_DEPARTMENTS = [
  { key: 'sales', label: 'Sales' },
  { key: 'operations', label: 'Operations' },
  { key: 'finance', label: 'Finance' },
  { key: 'technology', label: 'Technology' },
  { key: 'hr', label: 'HR' },
  { key: 'marketing', label: 'Marketing' },
];

const DEPARTMENT_PILL_STYLES = {
  sales: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  operations: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  technology: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  finance: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  marketing: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  hr: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
};

const DEPARTMENT_ACCENT_BORDER = {
  sales: 'border-red-300 dark:border-red-700',
  operations: 'border-blue-300 dark:border-blue-700',
  technology: 'border-purple-300 dark:border-purple-700',
  finance: 'border-green-300 dark:border-green-700',
  marketing: 'border-orange-300 dark:border-orange-700',
  hr: 'border-pink-300 dark:border-pink-700',
};

function departmentPillClass(department) {
  return (
    DEPARTMENT_PILL_STYLES[String(department || '').toLowerCase()] ||
    'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
  );
}

function departmentLabel(department) {
  const key = String(department || '').toLowerCase();
  if (key === 'general') return 'General';
  const found = PROGRESS_DEPARTMENTS.find((d) => d.key === key);
  return found ? found.label : null;
}

/** Composer targets: a general (whole-account) update plus the six departments. */
const NOTE_DEPT_OPTIONS = [{ key: 'general', label: 'General' }, ...PROGRESS_DEPARTMENTS];

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDate(date) {
  if (!date) return 'No date';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatRelativeDays(date) {
  if (!date) return '';
  const days = Math.round((Date.now() - date.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.round(days / 30);
  return months === 1 ? 'about a month ago' : `${months} months ago`;
}

function monthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(date) {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/** Flatten markdown into a short plain-text snippet. */
function markdownToSnippet(markdown, maxChars = 200) {
  const plain = String(markdown || '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  if (!plain) return '';
  return plain.length > maxChars ? `${plain.slice(0, maxChars).trimEnd()}…` : plain;
}

function actionItemText(item) {
  if (typeof item === 'string') return item.trim();
  return String(item?.description || item?.text || '').trim();
}

function isDepartmentKey(value) {
  return PROGRESS_DEPARTMENTS.some((d) => d.key === value);
}

/**
 * Resolve which department an event belongs to: explicit field first, then title
 * match, then the internal user on the record (their internal_users.team) — so
 * calls recorded or hosted by a team member land in that department's tab.
 */
function resolveDepartment(record, titleCandidates = [], teamDirectory = null) {
  const explicit = String(record?.department || '').toLowerCase();
  if (isDepartmentKey(explicit)) return explicit;
  const category = String(record?.category || '').toLowerCase();
  if (isDepartmentKey(category)) return category;
  for (const { key } of PROGRESS_DEPARTMENTS) {
    if (titleCandidates.some((t) => matchesDepartmentTitle(t, key))) return key;
  }
  if (teamDirectory) {
    const uidTeam = teamDirectory.byUid[String(record?.completedWith || '')];
    if (isDepartmentKey(uidTeam)) return uidTeam;
    const emails = [record?.recordedByEmail, ...(record?.inviteeEmails || [])];
    for (const email of emails) {
      const team = teamDirectory.byEmail[String(email || '').trim().toLowerCase()];
      if (isDepartmentKey(team)) return team;
    }
  }
  return null;
}

const checkInStatusStyles = {
  Good: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  Okay: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
  'Not Good': 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

/**
 * CRM Client Overview ▸ Progress tab: per-department view of everything the
 * client has been working on — call history with summaries/transcripts,
 * completed tasks, roadmap progress, monthly check-ins, and internal team
 * updates posted per department.
 */
export default function ClientProgressTab({ clientId, checkIns = [] }) {
  const { currentUser } = useAuth();
  const [calls, setCalls] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [roadmap, setRoadmap] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamDirectory, setTeamDirectory] = useState(null);
  const [activeDept, setActiveDept] = useState('all');
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(20);

  // Composer state
  const [noteDraft, setNoteDraft] = useState('');
  const [noteDept, setNoteDept] = useState('general');
  const [postingNote, setPostingNote] = useState(false);
  const [authorName, setAuthorName] = useState('');

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [callsResult, tasksResult, roadmapResult] = await Promise.allSettled([
        fetchAccountCalls(clientId),
        getDocs(query(collection(db, 'tasks'), where('companyId', '==', clientId))),
        clientRoadmapService.getClientRoadmap(clientId),
      ]);
      if (cancelled) return;
      setCalls(callsResult.status === 'fulfilled' ? callsResult.value : []);
      setTasks(
        tasksResult.status === 'fulfilled'
          ? tasksResult.value.docs.map((d) => ({ id: d.id, ...d.data() }))
          : []
      );
      setRoadmap(
        roadmapResult.status === 'fulfilled'
          ? (roadmapResult.value || []).filter((t) => (t.status || '') !== 'hidden')
          : []
      );
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Internal user directory: uid/email → team, for attributing calls to departments
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'internal_users'));
        if (cancelled) return;
        const byUid = {};
        const byEmail = {};
        for (const d of snap.docs) {
          const data = d.data() || {};
          const team = String(data.team || '').toLowerCase();
          byUid[d.id] = team;
          const email = String(data.email || '').trim().toLowerCase();
          if (email) byEmail[email] = team;
        }
        setTeamDirectory({ byUid, byEmail });
      } catch (error) {
        console.error('ClientProgressTab: internal users directory', error);
        if (!cancelled) setTeamDirectory({ byUid: {}, byEmail: {} });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Internal team progress notes — live feed
  useEffect(() => {
    if (!clientId) return;
    const notesQuery = query(
      collection(db, 'accounts', clientId, 'progressNotes'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(
      notesQuery,
      (snap) => setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (error) => console.error('ClientProgressTab: progress notes', error)
    );
    return unsub;
  }, [clientId]);

  // Resolve the poster's display name from internal_users
  useEffect(() => {
    if (!currentUser?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'internal_users', currentUser.uid));
        const data = snap.exists() ? snap.data() : null;
        if (!cancelled) {
          setAuthorName(
            data?.name || data?.displayName || currentUser.displayName || currentUser.email || 'Internal user'
          );
        }
      } catch {
        if (!cancelled) setAuthorName(currentUser.displayName || currentUser.email || 'Internal user');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, currentUser?.displayName, currentUser?.email]);

  const completedCalls = useMemo(
    () => calls.filter((c) => c.status === 'Completed' && c.date),
    [calls]
  );

  const completedCheckIns = useMemo(
    () =>
      (checkIns || []).filter(
        (c) => c.status !== 'pending' && (c.overallStatus || c.overallSatisfaction || c.rating)
      ),
    [checkIns]
  );

  // Per-department rollups for the overview cards
  const departmentStats = useMemo(() => {
    const stats = {};
    for (const { key } of PROGRESS_DEPARTMENTS) {
      stats[key] = {
        calls: 0,
        tasksDone: 0,
        notes: 0,
        roadmapDone: 0,
        roadmapTotal: 0,
        lastActivity: null,
      };
    }
    const bump = (dept, date) => {
      if (!dept || !stats[dept]) return;
      if (date && (!stats[dept].lastActivity || date > stats[dept].lastActivity)) {
        stats[dept].lastActivity = date;
      }
    };
    for (const call of completedCalls) {
      const dept = resolveDepartment(call, [call.title], teamDirectory);
      if (dept && stats[dept]) {
        stats[dept].calls += 1;
        bump(dept, call.date);
      }
    }
    for (const task of tasks) {
      if (!task.completed) continue;
      const dept = resolveDepartment(task, [task.title], teamDirectory);
      if (dept && stats[dept]) {
        stats[dept].tasksDone += 1;
        bump(dept, toDate(task.updatedAt) || toDate(task.createdAt));
      }
    }
    for (const note of notes) {
      const dept = String(note.department || '').toLowerCase();
      if (stats[dept]) {
        stats[dept].notes += 1;
        bump(dept, toDate(note.createdAt));
      }
    }
    for (const item of roadmap) {
      const dept = String(item.department || '').toLowerCase();
      if (!stats[dept]) continue;
      stats[dept].roadmapTotal += 1;
      const s = String(item.status || '').toLowerCase();
      if (s === 'completed' || s === 'done') stats[dept].roadmapDone += 1;
    }
    return stats;
  }, [completedCalls, tasks, notes, roadmap, teamDirectory]);

  // Unified timeline: calls + completed tasks + team notes + check-ins, newest first
  const timelineEvents = useMemo(() => {
    const events = [];

    for (const call of completedCalls) {
      events.push({
        id: `call-${call.id}`,
        type: 'call',
        date: call.date,
        department: resolveDepartment(call, [call.title], teamDirectory),
        call,
      });
    }
    for (const task of tasks) {
      if (!task.completed) continue;
      const date = toDate(task.updatedAt) || toDate(task.createdAt);
      if (!date) continue;
      events.push({
        id: `task-${task.id}`,
        type: 'task',
        date,
        department: resolveDepartment(task, [task.title], teamDirectory),
        task,
      });
    }
    for (const note of notes) {
      const date = toDate(note.createdAt) || new Date();
      events.push({
        id: `note-${note.id}`,
        type: 'note',
        date,
        department: String(note.department || '').toLowerCase() || null,
        note,
      });
    }
    for (const checkIn of completedCheckIns) {
      const date = toDate(checkIn.submittedAt);
      if (!date) continue;
      events.push({
        id: `checkin-${checkIn.id}`,
        type: 'checkin',
        date,
        department: null,
        checkIn,
      });
    }

    return events.sort((a, b) => b.date - a.date);
  }, [completedCalls, tasks, notes, completedCheckIns, teamDirectory]);

  const filteredEvents = useMemo(() => {
    if (activeDept === 'all') return timelineEvents;
    return timelineEvents.filter((e) => e.department === activeDept);
  }, [timelineEvents, activeDept]);

  const visibleEvents = filteredEvents.slice(0, visibleCount);

  // Group visible events by month for timeline headers
  const groupedEvents = useMemo(() => {
    const groups = [];
    let current = null;
    for (const event of visibleEvents) {
      const key = monthKey(event.date);
      if (!current || current.key !== key) {
        current = { key, label: monthLabel(event.date), events: [] };
        groups.push(current);
      }
      current.events.push(event);
    }
    return groups;
  }, [visibleEvents]);

  const handleSelectDept = useCallback((dept) => {
    setActiveDept((prev) => (prev === dept ? 'all' : dept));
    setVisibleCount(20);
    setExpandedEventId(null);
  }, []);

  const handlePostNote = async () => {
    const text = noteDraft.trim();
    if (!text || postingNote || !clientId) return;
    setPostingNote(true);
    try {
      await addDoc(collection(db, 'accounts', clientId, 'progressNotes'), {
        text,
        department: noteDept,
        byUid: currentUser?.uid || '',
        byName: authorName || 'Internal user',
        createdAt: serverTimestamp(),
      });
      setNoteDraft('');
    } catch (error) {
      console.error('ClientProgressTab: post note', error);
      alert('Failed to post the update. Please try again.');
    } finally {
      setPostingNote(false);
    }
  };

  const handleDeleteNote = async (note) => {
    if (!note?.id || note.byUid !== currentUser?.uid) return;
    if (!window.confirm('Delete this update?')) return;
    try {
      await deleteDoc(doc(db, 'accounts', clientId, 'progressNotes', note.id));
    } catch (error) {
      console.error('ClientProgressTab: delete note', error);
      alert('Failed to delete the update.');
    }
  };

  const renderCallEvent = (event) => {
    const { call } = event;
    const expanded = expandedEventId === event.id;
    const parsed = parseFathomSummarySections(call.summary || '');
    const snippet = markdownToSnippet(parsed.keyTakeaways || call.summary || '');
    const callActionItems = (call.actionItems || [])
      .map((item) => ({
        text: actionItemText(item),
        assigneeName: typeof item === 'object' ? item?.assigneeName || null : null,
        completed: Boolean(item?.completed),
      }))
      .filter((i) => i.text);

    return (
      <div className="min-w-0 flex-1">
        <button
          type="button"
          className="w-full text-left"
          onClick={() => setExpandedEventId(expanded ? null : event.id)}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-gray-900 dark:text-white text-sm">
                  {call.title}
                </span>
                {event.department && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${departmentPillClass(event.department)}`}>
                    {getPortalEmoji(event.department)} {departmentLabel(event.department)}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                📞 Call · {formatDate(event.date)} · {formatRelativeDays(event.date)}
              </p>
              {!expanded && snippet && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1.5 line-clamp-2">{snippet}</p>
              )}
            </div>
            <span className="text-gray-400 dark:text-gray-500 mt-0.5 shrink-0">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          </div>
        </button>
        {expanded && (
          <div className="mt-3 space-y-3">
            {parsed.meetingPurpose && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Meeting Purpose
                </p>
                <MeetingSummaryContent markdown={parsed.meetingPurpose} />
              </div>
            )}
            {parsed.keyTakeaways && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Key Takeaways
                </p>
                <MeetingSummaryContent markdown={parsed.keyTakeaways} />
              </div>
            )}
            {parsed.topics && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Topics
                </p>
                <MeetingSummaryContent markdown={parsed.topics} />
              </div>
            )}
            {parsed.nextSteps && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Next Steps
                </p>
                <MeetingSummaryContent markdown={parsed.nextSteps} />
              </div>
            )}
            {!parsed.hasStructuredSections && call.summary && (
              <MeetingSummaryContent markdown={call.summary} />
            )}
            {!call.summary && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No summary was recorded for this call.
              </p>
            )}
            {callActionItems.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Action Items from this Call
                </p>
                <ul className="space-y-1">
                  {callActionItems.map((item, index) => (
                    <li key={index} className="text-sm text-gray-700 dark:text-gray-300 flex gap-2">
                      <span className="shrink-0">{item.completed ? '✅' : '⬜'}</span>
                      <span>
                        {item.text}
                        {item.assigneeName && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                            ({item.assigneeName})
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {call.recordingUrl && (
              <a
                href={call.recordingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 hover:underline"
              >
                <Video className="w-4 h-4" />
                Watch recording
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTaskEvent = (event) => (
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-gray-900 dark:text-white text-sm">{event.task.title}</span>
        {event.department && (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${departmentPillClass(event.department)}`}>
            {getPortalEmoji(event.department)} {departmentLabel(event.department)}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
        ✅ Task completed · {formatDate(event.date)} · {formatRelativeDays(event.date)}
      </p>
      {event.task.description && (
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1.5 line-clamp-2">
          {event.task.description}
        </p>
      )}
    </div>
  );

  const renderNoteEvent = (event) => (
    <div className="min-w-0 flex-1">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 dark:text-white text-sm">
              {event.note.byName || 'Internal user'}
            </span>
            {event.department && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${departmentPillClass(event.department)}`}>
                {getPortalEmoji(event.department)} {departmentLabel(event.department)}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            📝 Team update · {formatDate(event.date)} · {formatRelativeDays(event.date)}
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-200 mt-1.5 whitespace-pre-wrap">
            {event.note.text}
          </p>
        </div>
        {event.note.byUid === currentUser?.uid && (
          <button
            type="button"
            onClick={() => handleDeleteNote(event.note)}
            className="text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400 shrink-0 mt-0.5"
            title="Delete this update"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );

  const renderCheckInEvent = (event) => {
    const { checkIn } = event;
    const rating = checkIn.overallSatisfaction ?? checkIn.rating;
    return (
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900 dark:text-white text-sm">
            {checkIn.month || 'Monthly'} check-in submitted
          </span>
          {checkIn.overallStatus && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${checkInStatusStyles[checkIn.overallStatus] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
              {checkIn.overallStatus}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          💓 Check-in · {formatDate(event.date)} · {formatRelativeDays(event.date)}
          {rating ? ` · ${rating}/5 satisfaction` : ''}
        </p>
        {(checkIn.comments || checkIn.additionalFeedback) && (
          <p className="text-sm text-gray-600 dark:text-gray-300 italic mt-1.5 line-clamp-3">
            “{checkIn.comments || checkIn.additionalFeedback}”
          </p>
        )}
      </div>
    );
  };

  const EVENT_MARKER_EMOJI = { call: '📞', task: '✅', note: '📝', checkin: '💓' };

  return (
    <div className="space-y-6">
      {/* Department overview cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {PROGRESS_DEPARTMENTS.map(({ key, label }) => {
          const stats = departmentStats[key];
          const active = activeDept === key;
          const hasActivity = stats.calls > 0 || stats.tasksDone > 0 || stats.notes > 0;
          return (
            <button
              key={key}
              type="button"
              onClick={() => handleSelectDept(key)}
              className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-3 text-center border-2 transition-all hover:shadow-md ${
                active ? DEPARTMENT_ACCENT_BORDER[key] : 'border-transparent'
              } ${hasActivity ? '' : 'opacity-60'}`}
            >
              <div className="text-2xl mb-1">{getPortalEmoji(key)}</div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{label}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {stats.calls} {stats.calls === 1 ? 'call' : 'calls'} · {stats.tasksDone}{' '}
                {stats.tasksDone === 1 ? 'task' : 'tasks'}
              </p>
              {stats.roadmapTotal > 0 && (
                <div className="mt-2">
                  <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-red-500 rounded-full transition-all"
                      style={{ width: `${Math.round((stats.roadmapDone / stats.roadmapTotal) * 100)}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                    {stats.roadmapDone}/{stats.roadmapTotal} roadmap
                  </p>
                </div>
              )}
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                {stats.lastActivity ? `Active ${formatRelativeDays(stats.lastActivity)}` : 'No activity yet'}
              </p>
            </button>
          );
        })}
      </div>

      {/* Post an internal update */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <StickyNote className="w-5 h-5 text-red-600" />
          <h3 className="font-semibold text-gray-900 dark:text-white">Post a Team Update</h3>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={noteDept}
            onChange={(e) => setNoteDept(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-red-600 sm:w-44"
          >
            {NOTE_DEPT_OPTIONS.map(({ key, label }) => (
              <option key={key} value={key}>
                {getPortalEmoji(key)} {label}
              </option>
            ))}
          </select>
          <textarea
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            rows={1}
            placeholder="Share progress, wins, or context the team should know about…"
            className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-red-600 resize-y min-h-[38px]"
          />
          <button
            type="button"
            onClick={handlePostNote}
            disabled={postingNote || !noteDraft.trim()}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-4 h-4" />
            {postingNote ? 'Posting…' : 'Post'}
          </button>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-red-600" />
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {activeDept === 'all'
                ? 'Progress Timeline'
                : `${getPortalEmoji(activeDept)} ${departmentLabel(activeDept)} Timeline`}
            </h3>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={() => handleSelectDept('all')}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                activeDept === 'all'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              All
            </button>
            {PROGRESS_DEPARTMENTS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => handleSelectDept(key)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  activeDept === key
                    ? 'bg-red-600 text-white'
                    : `${departmentPillClass(key)} hover:opacity-80`
                }`}
              >
                {getPortalEmoji(key)} {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading progress…</p>
        ) : filteredEvents.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {activeDept === 'all'
              ? 'No calls, completed tasks, or team updates yet for this client.'
              : `No ${departmentLabel(activeDept)} activity yet — post the first update above.`}
          </p>
        ) : (
          <div className="space-y-6">
            {groupedEvents.map((group) => (
              <div key={group.key}>
                <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">
                  {group.label}
                </p>
                <div className="space-y-4 border-l-2 border-gray-100 dark:border-gray-700 ml-2">
                  {group.events.map((event) => (
                    <div key={event.id} className="relative pl-6">
                      <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 flex items-center justify-center text-xs">
                        {EVENT_MARKER_EMOJI[event.type] || '•'}
                      </span>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-sm transition-shadow flex">
                        {event.type === 'call' && renderCallEvent(event)}
                        {event.type === 'task' && renderTaskEvent(event)}
                        {event.type === 'note' && renderNoteEvent(event)}
                        {event.type === 'checkin' && renderCheckInEvent(event)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {filteredEvents.length > visibleCount && (
              <button
                type="button"
                onClick={() => setVisibleCount((prev) => prev + 20)}
                className="w-full text-center text-sm text-red-600 dark:text-red-400 hover:underline py-1"
              >
                Show {Math.min(filteredEvents.length - visibleCount, 20)} more
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
